/** Site-level shift configuration (comes from site settings) */
export interface ShiftConfig {
  shiftsPerDay: number;        // e.g. 2 (12 h) or 3 (8 h)
  shiftDurationHours: number;  // e.g. 12 or 8
  shiftLabels: string[];       // e.g. ['Day', 'Night'] or ['Morning', 'Afternoon', 'Night']
  /**
   * Local wall-clock hour (0–23) at which each shift starts.
   * Must have the same length as shiftLabels.
   * e.g. [0, 12] for 2×12 h, or [8, 16, 0] for 3×8 h starting at 08:00.
   */
  shiftStartHours: number[];
}

/** API DTO matching backend SiteShiftConfigDto */
export interface SiteShiftConfigApiDto {
  siteId: number;
  shiftsPerDay: number;
  shiftLabels: string[];
  firstShiftStartHour: number;
}

/** Converts a persisted SiteShiftConfigApiDto to the runtime ShiftConfig. */
export function shiftConfigFromDto(dto: SiteShiftConfigApiDto): ShiftConfig {
  const duration = 24 / dto.shiftsPerDay;
  return {
    shiftsPerDay: dto.shiftsPerDay,
    shiftDurationHours: duration,
    shiftLabels: dto.shiftLabels,
    shiftStartHours: Array.from({ length: dto.shiftsPerDay }, (_, i) =>
      (dto.firstShiftStartHour + i * duration) % 24,
    ),
  };
}

/** Raw DTO matching the backend DrillSessionDto (camelCase JSON) */
export interface DrillSessionApiDto {
  id: string;
  blastId: number;
  drillRigId?: number;
  drillRigName?: string;
  operatorId?: number;
  operatorName?: string;
  sessionStartUtc: string;    // ISO string
  sessionEndUtc?: string;
  totalDrilledLength: number;
  engineHoursStart?: number;
  engineHoursEnd?: number;
  drifterHoursStart?: number;
  drifterHoursEnd?: number;
  totalPauseHours?: number;
  numberOfHolesDrilled: number;
  holeIds?: string[];
}

/** One drilling session = one rig on one blast on one shift */
export interface DrillingSession {
  id: string;                 // backend session ID (needed for edit)
  blastId: string;
  equipmentId: number;        // rig primary key
  equipmentLabel: string;     // e.g. 'RIG-04'
  operatorId: number;
  operatorLabel: string;      // e.g. 'J. Martinez'
  date: string;               // ISO date 'YYYY-MM-DD'
  shiftIndex: number;         // 0-based index within the day
  shiftDurationHours: number; // total shift duration (from site config)
  engineHours: number;        // actual engine operation hours (<= shiftDurationHours)
  pauseHours: number;         // pause duration within shift
  drilledMeters: number;      // meters drilled in this session
  rawDto?: DrillSessionApiDto; // original API DTO, used to pre-fill the edit form
}

/** One blast row in the activity chart */
export interface BlastRow {
  blastId: number;
  projectId: number;          // owning project (used for navigation)
  label: string;              // e.g. 'BLT-0412'
  zone: string;               // e.g. 'Pit-3 / Bench 44'
  holeCount: number;
  targetMeters: number;       // sum of all blast hole lengths (known in advance)
  blastDate: string | null;   // ISO date when the blast fires (null if not yet scheduled)
  sessions: DrillingSession[];
}

/** Color palette entry */
export interface ColorEntry {
  id: number;    // rigId or operatorId
  label: string;
  color: string; // hex
}

/** Input shape for BlastDrillingPanelComponent */
export interface BlastDrillingData {
  blastId: number;
  label: string;              // e.g. 'BLT-0412'
  zone: string;               // e.g. 'Pit-3 / Bench 44'
  holeCount: number;
  targetMeters: number;       // planned total drilled length (sum of hole depths)
  blastDate: string | null;
  sessions: DrillingSession[];
}
